import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import img1 from '../../../assets/attendenceCarousel.png';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import { attendanceApi } from '../services/api';
import { useAttendanceCache } from '../context/AttendanceContext';
import { ChevronDown, Download, Filter, Eye } from 'lucide-react';

const ITEMS_PER_PAGE = 20;

// Column configuration for Regular employees
const REGULAR_COLUMNS = [
  { key: 'sno', label: 'S No.', visible: true },
  { key: 'empName', label: 'Employee Name', visible: true },
  { key: 'kvName', label: 'KV Name', visible: true },
  { key: 'roName', label: 'RO Name', visible: true },
  { key: 'empType', label: 'Employee Type', visible: true },
  { key: 'presentSchoolName', label: 'Present School', visible: false },
  { key: 'stateName', label: 'State', visible: false },
  { key: 'districtName', label: 'District', visible: false },
  { key: 'empGender', label: 'Gender', visible: false },
  { key: 'empCategoryName', label: 'Category', visible: false },
  { key: 'totalAttendance', label: 'Total Attendance', visible: true },
  { key: 'details', label: 'Details', visible: true },
];

// Column configuration for Contractual employees
const CONTRACTUAL_COLUMNS = [
  { key: 'sno', label: 'S No.', visible: true },
  { key: 'empName', label: 'Employee Name', visible: true },
  { key: 'empPost', label: 'Post', visible: true },
  { key: 'subject', label: 'Subject', visible: true },
  { key: 'presentSchoolName', label: 'Present School', visible: false },
  { key: 'stateName', label: 'State', visible: false },
  { key: 'districtName', label: 'District', visible: false },
  { key: 'dateOfJoining', label: 'Date of Joining', visible: true },
  { key: 'gender', label: 'Gender', visible: true },
  { key: 'totalAttendance', label: 'Total Attendance', visible: true },
  { key: 'details', label: 'Details', visible: true },
];

export default function EmployeeAttendance() {
  const carouselImages = [img1];
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _role = params.get('role') === 'student' ? 'student' : 'teacher';
  const filter = params.get('filter') || 'all';

  const cache = useAttendanceCache();
  const [currentPage, setCurrentPage] = useState(1);
  const [employeeData, setEmployeeData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [totalPages, _setTotalPages] = useState(10);
  const [employeeType, setEmployeeType] = useState<'regular' | 'contractual'>('regular');

  // Date selectors (year in two-digit format as API expects)
  // Selection state: empty means user hasn't selected that level
  const [selectedYear, setSelectedYear] = useState<number | ''>('');
  const [selectedMonth, setSelectedMonth] = useState<number | ''>('');
  const [selectedDate, setSelectedDate] = useState<number | ''>('');

  // Latest day info (filled from API on mount) used when user hasn't selected anything
  const [latestDay, setLatestDay] = useState<{ year: number; month: number; date: number } | null>(null);
  const [useLatest, setUseLatest] = useState(true); // true until user changes any selector

  // Draft selections (not committed until Apply button)
  const [draftYear, setDraftYear] = useState<number | ''>('');
  const [draftMonth, setDraftMonth] = useState<number | ''>('');
  const [draftDate, setDraftDate] = useState<number | ''>('');
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Initialize draft from current selection
  useEffect(() => {
    if (selectedYear !== '') setDraftYear(selectedYear);
    if (selectedMonth !== '') setDraftMonth(selectedMonth);
    if (selectedDate !== '') setDraftDate(selectedDate);
  }, [selectedYear, selectedMonth, selectedDate]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _months = [
    { value: '', label: 'Month' },
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];


  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  // Column-based filter selections (key -> value)
  const [filterSelections, setFilterSelections] = useState<Record<string, string>>({});

  // Get current columns based on employee type
  const currentColumns = employeeType === 'regular' ? REGULAR_COLUMNS : CONTRACTUAL_COLUMNS;

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    currentColumns.reduce((acc, col) => ({ ...acc, [col.key]: col.visible }), {})
  );

  // Reset visible columns when employee type changes
  useEffect(() => {
    setVisibleColumns(
      currentColumns.reduce((acc, col) => ({ ...acc, [col.key]: col.visible }), {})
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeType]);

  // Fetch latest day info on mount when selectors are empty
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await attendanceApi.getDailyAttendanceByKVSNoPaging();
        if (!mounted) return;
        if (res && res.data && res.meta && res.meta.date) {
          const d = res.meta.date; // assume format 'YYYY-MM-DD' or { year, month, date }
          let year = new Date().getFullYear() % 100;
          let month = new Date().getMonth() + 1;
          let date = new Date().getDate();

          if (typeof d === 'string') {
            const dt = new Date(d);
            year = dt.getFullYear() % 100;
            month = dt.getMonth() + 1;
            date = dt.getDate();
          } else if (d && typeof d === 'object') {
            year = Number(d.year ?? year);
            month = Number(d.month ?? month);
            date = Number(d.date ?? date);
          }

          setLatestDay({ year, month, date });

          // If user hasn't selected anything, show latest data
          if (useLatest && selectedYear === '' && selectedMonth === '' && selectedDate === '') {
            setEmployeeData(res.data);
            // cache under special key 'latest'
            if (employeeType === 'regular') cache.setCachedRegularData(`latest|t:${employeeType}` as any, res.data);
            else cache.setCachedContractualData(`latest|t:${employeeType}` as any, res.data);
          }
        }
      } catch (err) {
        // ignore
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      // Build cache key including date params and employee type; if no selection and useLatest true use 'latest'
      const effectiveYear = selectedYear === '' && useLatest && latestDay ? latestDay.year : (selectedYear === '' ? undefined : Number(selectedYear));
      const effectiveMonth = selectedMonth === '' && useLatest && latestDay ? latestDay.month : (selectedMonth === '' ? undefined : Number(selectedMonth));
      const effectiveDate = selectedDate === '' && useLatest && latestDay ? latestDay.date : (selectedDate === '' ? undefined : Number(selectedDate));

      const key = (effectiveYear === undefined && effectiveMonth === undefined && effectiveDate === undefined && useLatest) ? `latest|t:${employeeType}` : `${currentPage}|y:${effectiveYear ?? ''}|m:${effectiveMonth ?? ''}|d:${effectiveDate ?? ''}|t:${employeeType}`;

      // Check cache first
      let cachedData = null;
      if (employeeType === 'regular') {
        cachedData = cache.getCachedRegularData(key as any);
      } else {
        cachedData = cache.getCachedContractualData(key as any);
      }

      if (cachedData) {
        setEmployeeData(cachedData);
        return;
      }

      // Fetch from API if not in cache
      setLoading(true);
      try {
        // Use getStaffAttendanceRegister with emptype parameter for both regular and contractual
        const response = await attendanceApi.getStaffAttendanceRegister(
          currentPage,
          effectiveYear,
          effectiveMonth,
          effectiveDate,
          employeeType
        );

        if (response && response.data) {
          setEmployeeData(response.data);
          // Cache the data with composite key
          if (employeeType === 'regular') {
            cache.setCachedRegularData(key as any, response.data);
          } else {
            cache.setCachedContractualData(key as any, response.data);
          }
        }
      } catch (error) {
        console.error('Error fetching employee attendance:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [currentPage, filter, employeeType, cache, selectedYear, selectedMonth, selectedDate, latestDay, useLatest]);

  // Compute filter options from current employeeData
  const filterOptions = useMemo(() => {
    const opts: Record<string, string[]> = {};
    if (!employeeData || employeeData.length === 0) return opts;

    const add = (key: string, val: any) => {
      if (val === undefined || val === null || String(val) === '') return;
      opts[key] = opts[key] || [];
      const str = String(val);
      if (!opts[key].includes(str)) opts[key].push(str);
    };

    employeeData.forEach(emp => {
      if (employeeType === 'regular') {
        add('kvName', emp.kvName);
        add('roName', emp.roName);
        add('stateName', emp.stateName);
        add('districtName', emp.districtName);
        add('presentSchoolName', emp.presentSchoolName);
        add('empCategoryName', emp.empCategoryName);
        add('empGender', emp.empGender ?? emp.gender);
        add('empType', emp.empType);
      } else {
        add('empPost', emp.empPost);
        add('subject', emp.subject);
        add('stateName', emp.stateName);
        add('districtName', emp.districtName);
        add('presentSchoolName', emp.presentSchoolName);
        add('gender', emp.gender);
      }
    });

    // Sort options alphabetically (strings)
    Object.keys(opts).forEach(k => opts[k].sort((a, b) => a.localeCompare(b)));
    return opts;
  }, [employeeData, employeeType]);

  // Filtered data based on search term and column filters
  const filteredData = useMemo(() => {
    let data = employeeData || [];

    // Apply column filters first
    Object.entries(filterSelections).forEach(([field, value]) => {
      if (value === undefined || value === null || String(value) === '') return;
      const valLower = String(value).toLowerCase();
      data = data.filter(emp => {
        const fieldVal = emp[field];
        if (field === 'empType') {
          // Normalize empType numeric to '1'/'2'
          return String(fieldVal) === String(value);
        }
        if (field === 'gender') {
          return String(fieldVal) === String(value);
        }
        return String(fieldVal || '').toLowerCase().includes(valLower);
      });
    });

    // Then apply free-text search
    if (!searchTerm.trim()) return data;
    const lowerSearch = searchTerm.toLowerCase();
    return data.filter(emp => {
      if (employeeType === 'regular') {
        return (
          String(emp.empName || '').toLowerCase().includes(lowerSearch) ||
          String(emp.kvName || '').toLowerCase().includes(lowerSearch) ||
          String(emp.roName || '').toLowerCase().includes(lowerSearch) ||
          (emp.empType === 1 ? 'teaching' : 'non-teaching').includes(lowerSearch)
        );
      }
      return (
        String(emp.empName || '').toLowerCase().includes(lowerSearch) ||
        String(emp.empPost || '').toLowerCase().includes(lowerSearch) ||
        (emp.gender === 1 ? 'male' : emp.gender === 2 ? 'female' : '').includes(lowerSearch)
      );
    });
  }, [employeeData, searchTerm, employeeType, filterSelections]);

  // Column visibility toggle
  const toggleColumn = (columnKey: string) => {
    setVisibleColumns(prev => ({ ...prev, [columnKey]: !prev[columnKey] }));
  };

  // Download handlers
  const handleDownload = (format: 'csv' | 'excel' | 'pdf' | 'copy') => {
    // Prepare data for export
    const exportData = filteredData.map((emp, idx) => {
      if (employeeType === 'regular') {
        const empTypeText = emp.empType === 1 ? 'Teaching' : emp.empType === 2 ? 'Non-Teaching' : 'Unknown';
        return {
          'S No.': (currentPage - 1) * ITEMS_PER_PAGE + idx + 1,
          'Employee Name': emp.empName,
          'KV Name': emp.kvName || 'N/A',
          'RO Name': emp.roName || 'N/A',
          'Employee Type': empTypeText,
          'Present School': emp.presentSchoolName || 'N/A',
          'State': emp.stateName || 'N/A',
          'District': emp.districtName || 'N/A',
          'Gender': emp.empGender === 1 || emp.gender === 1 ? 'Male' : emp.empGender === 2 || emp.gender === 2 ? 'Female' : 'N/A',
          'Category': emp.empCategoryName || 'N/A',
          'Total Attendance': emp.totalAttendance || 0
        };
      } else {
        const genderText = emp.gender === 1 ? 'Male' : emp.gender === 2 ? 'Female' : 'N/A';
        const joiningDate = emp.dateOfJoining ? new Date(emp.dateOfJoining).toLocaleDateString('en-GB') : 'N/A';
        return {
          'S No.': (currentPage - 1) * ITEMS_PER_PAGE + idx + 1,
          'Employee Name': emp.empName,
          'Post': emp.empPost || 'N/A',
          'Subject': emp.subject || 'N/A',
          'Present School': emp.presentSchoolName || 'N/A',
          'State': emp.stateName || 'N/A',
          'District': emp.districtName || 'N/A',
          'Date of Joining': joiningDate,
          'Gender': genderText,
          'Total Attendance': emp.totalAttendance || 0
        };
      }
    });

    if (format === 'csv') {
      const csvContent = [
        Object.keys(exportData[0] || {}).join(','),
        ...exportData.map(row => Object.values(row).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `employee_attendance_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } else if (format === 'copy') {
      const textContent = exportData.map(row => Object.values(row).join('\t')).join('\n');
      navigator.clipboard.writeText(textContent);
      alert('Data copied to clipboard!');
    } else if (format === 'excel') {
      alert('Excel export feature requires additional library. CSV format exported instead.');
      handleDownload('csv');
    } else if (format === 'pdf') {
      alert('PDF export feature requires additional library.');
    }

    setShowDownloadMenu(false);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="min-h-screen flex flex-col gap-0 bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />
      <div className="w-full max-w-7xl px-4 pt-2 sm:pt-3">
        <Breadcrumb />
      </div>
      <main className="flex-grow w-full max-w-8xl mx-auto px-4 py-5 md:px-8 lg:px-16">
        <h2 className="text-4xl font-semibold text-[#6A1786] mb-6">Employee</h2>

        {/* Control Bar - Search, Switch, Column Selector, Filter, Download */}
        <div className="flex justify-between items-center mb-4 gap-3 bg-white rounded-lg shadow p-4">
          {/* Search Bar */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <input
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          <div className="flex gap-2 items-center">
            {/* Employee Type Switch */}

            <div className="flex items-center gap-3 px-3 py-1.5 bg-gray-50 rounded-lg">
              <span className={`text-xs font-semibold transition-colors ${employeeType === 'regular' ? 'text-[#6A1786]' : 'text-gray-400'}`}>
                Regular
              </span>
              <label className="relative block aspect-[2/0.75] w-10 rounded-full bg-gradient-to-br from-purple-100 via-violet-600 shadow-lg shadow-purple-200 transition-all duration-300 cursor-pointer">
                <input
                  className="peer/input hidden"
                  type="checkbox"
                  checked={employeeType === 'contractual'}
                  onChange={(e) => {
                    setEmployeeType(e.target.checked ? 'contractual' : 'regular');
                    setCurrentPage(1);
                  }}
                />
                <div className="absolute left-[3%] top-1/2 aspect-square h-[90%] -translate-y-1/2 rotate-180 rounded-full bg-white bg-gradient-to-t transition-all duration-500 peer-checked/input:left-[63%] peer-checked/input:-rotate-6 shadow-md"></div>
              </label>
              <span className={`text-xs font-semibold transition-colors ${employeeType === 'contractual' ? 'text-[#6A1786]' : 'text-gray-400'}`}>
                Contractual
              </span>
            </div>

            {/* Hierarchical date selector: Year → Month → Date */}
            <div className="flex items-center gap-2 relative">
              {/* Display selected date or placeholder */}
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:border-[#6A1786] transition-colors min-w-[200px] text-left flex justify-between items-center"
              >
                <span>
                  {draftYear === '' ? 'Select Date' :
                    draftDate !== '' ? `${draftDate}/${draftMonth}/${2000 + Number(draftYear)}` :
                      draftMonth !== '' ? `${draftMonth}/${2000 + Number(draftYear)}` :
                        `${2000 + Number(draftYear)}`}
                </span>
                <ChevronDown size={16} className={`transition-transform ${showDatePicker ? 'rotate-180' : ''}`} />
              </button>

              {/* Hierarchical picker dropdown */}
              {showDatePicker && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 p-4 min-w-[320px]">
                  {/* Year selection */}
                  <div className="mb-3">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Year</label>
                    <select
                      value={draftYear}
                      onChange={(e) => {
                        setDraftYear(e.target.value === '' ? '' : Number(e.target.value));
                        setDraftMonth('');
                        setDraftDate('');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786]"
                    >
                      <option value="">Select Year</option>
                      {Array.from({ length: 10 }, (_, i) => {
                        const year = new Date().getFullYear() - i;
                        return <option key={year} value={year % 100}>{year}</option>;
                      })}
                    </select>
                  </div>

                  {/* Month selection (only if year is selected) */}
                  {draftYear !== '' && (
                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Month (optional)</label>
                      <select
                        value={draftMonth}
                        onChange={(e) => {
                          setDraftMonth(e.target.value === '' ? '' : Number(e.target.value));
                          setDraftDate('');
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786]"
                      >
                        <option value="">Select Month</option>
                        <option value="1">January</option>
                        <option value="2">February</option>
                        <option value="3">March</option>
                        <option value="4">April</option>
                        <option value="5">May</option>
                        <option value="6">June</option>
                        <option value="7">July</option>
                        <option value="8">August</option>
                        <option value="9">September</option>
                        <option value="10">October</option>
                        <option value="11">November</option>
                        <option value="12">December</option>
                      </select>
                    </div>
                  )}

                  {/* Date selection (only if month is selected) */}
                  {draftMonth !== '' && (
                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Date (optional)</label>
                      <select
                        value={draftDate}
                        onChange={(e) => setDraftDate(e.target.value === '' ? '' : Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786]"
                      >
                        <option value="">Select Date</option>
                        {Array.from({ length: new Date(2000 + Number(draftYear), Number(draftMonth), 0).getDate() }, (_, i) => i + 1).map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => {
                        setDraftYear('');
                        setDraftMonth('');
                        setDraftDate('');
                      }}
                      className="flex-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm transition-colors"
                    >
                      Clear
                    </button>
                    <button
                      onClick={() => {
                        if (draftYear !== '') {
                          setSelectedYear(draftYear);
                          setSelectedMonth(draftMonth);
                          setSelectedDate(draftDate);
                          setUseLatest(false);
                          setCurrentPage(1);
                          setShowDatePicker(false);
                        }
                      }}
                      disabled={draftYear === ''}
                      className="flex-1 px-3 py-2 bg-[#6A1786] hover:bg-purple-800 text-white rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={() => {
                  setSelectedYear('');
                  setSelectedMonth('');
                  setSelectedDate('');
                  setDraftYear('');
                  setDraftMonth('');
                  setDraftDate('');
                  setUseLatest(true);
                  setCurrentPage(1);
                  setShowDatePicker(false);
                }}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm transition-colors"
              >
                Latest
              </button>
            </div>



            {/* Filter Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-all"
              >
                <Filter size={16} />
                Filter
                <ChevronDown size={16} className={`transition-transform ${showFilterMenu ? 'rotate-180' : ''}`} />
              </button>

              {showFilterMenu && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[220px] py-3 px-3">
                  {/* Dynamic filter controls based on employeeType */}
                  {employeeType === 'regular' ? (
                    <div className="space-y-2">
                      <label className="text-xs text-gray-600">KV Name</label>
                      <select
                        value={filterSelections.kvName || ''}
                        onChange={(e) => setFilterSelections(prev => ({ ...prev, kvName: e.target.value }))}
                        className="w-full px-2 py-1 border rounded text-sm"
                      >
                        <option value="">All KVs</option>
                        {(filterOptions.kvName || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>

                      <label className="text-xs text-gray-600">RO Name</label>
                      <select
                        value={filterSelections.roName || ''}
                        onChange={(e) => setFilterSelections(prev => ({ ...prev, roName: e.target.value }))}
                        className="w-full px-2 py-1 border rounded text-sm"
                      >
                        <option value="">All ROs</option>
                        {(filterOptions.roName || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>

                      <label className="text-xs text-gray-600">Employee Type</label>
                      <select
                        value={filterSelections.empType || ''}
                        onChange={(e) => setFilterSelections(prev => ({ ...prev, empType: e.target.value }))}
                        className="w-full px-2 py-1 border rounded text-sm"
                      >
                        <option value="">Any</option>
                        <option value="1">Teaching</option>
                        <option value="2">Non-Teaching</option>
                      </select>

                      <div className="flex gap-2 mt-2">
                        <button onClick={() => { setFilterSelections({}); setShowFilterMenu(false); setSearchTerm(''); }} className="flex-1 px-3 py-2 bg-gray-100 rounded text-sm">Clear Filters</button>
                        <button onClick={() => { setShowFilterMenu(false); setCurrentPage(1); }} className="flex-1 px-3 py-2 bg-purple-600 text-white rounded text-sm">Apply</button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <label className="text-xs text-gray-600">Post</label>
                      <select
                        value={filterSelections.empPost || ''}
                        onChange={(e) => setFilterSelections(prev => ({ ...prev, empPost: e.target.value }))}
                        className="w-full px-2 py-1 border rounded text-sm"
                      >
                        <option value="">All Posts</option>
                        {(filterOptions.empPost || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>

                      <label className="text-xs text-gray-600">Subject</label>
                      <select
                        value={filterSelections.subject || ''}
                        onChange={(e) => setFilterSelections(prev => ({ ...prev, subject: e.target.value }))}
                        className="w-full px-2 py-1 border rounded text-sm"
                      >
                        <option value="">All Subjects</option>
                        {(filterOptions.subject || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>

                      <label className="text-xs text-gray-600">Gender</label>
                      <select
                        value={filterSelections.gender || ''}
                        onChange={(e) => setFilterSelections(prev => ({ ...prev, gender: e.target.value }))}
                        className="w-full px-2 py-1 border rounded text-sm"
                      >
                        <option value="">Any</option>
                        <option value="1">Male</option>
                        <option value="2">Female</option>
                      </select>

                      <div className="flex gap-2 mt-2">
                        <button onClick={() => { setFilterSelections({}); setShowFilterMenu(false); setSearchTerm(''); }} className="flex-1 px-3 py-2 bg-gray-100 rounded text-sm">Clear Filters</button>
                        <button onClick={() => { setShowFilterMenu(false); setCurrentPage(1); }} className="flex-1 px-3 py-2 bg-purple-600 text-white rounded text-sm">Apply</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Employee Attendance Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="flex justify-end items-center gap-3 px-4 py-3 border-b bg-white">
            {/* Column Visibility Dropdown (moved here) */}
            <div className="relative">
              <button
                onClick={() => setShowColumnMenu(!showColumnMenu)}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-all"
              >
                <Eye size={16} />
                Columns
                <ChevronDown size={16} className={`transition-transform ${showColumnMenu ? 'rotate-180' : ''}`} />
              </button>
              {showColumnMenu && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[200px] py-2">
                  {currentColumns.map(col => (
                    <label key={col.key} className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                      <input type="checkbox" checked={visibleColumns[col.key]} onChange={() => toggleColumn(col.key)} className="rounded text-purple-600 focus:ring-purple-500" />
                      <span className="text-gray-700">{col.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Download Dropdown (moved here) */}
            <div className="relative">
              <button
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-all shadow-sm hover:shadow-md"
              >
                <Download size={16} />
                Download
                <ChevronDown size={16} className={`transition-transform ${showDownloadMenu ? 'rotate-180' : ''}`} />
              </button>
              {showDownloadMenu && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[150px] py-1">
                  <button onClick={() => handleDownload('copy')} className="w-full text-left px-4 py-2 hover:bg-blue-50 text-gray-700 text-sm border-b border-gray-100 transition-colors">📋 Copy</button>
                  <button onClick={() => handleDownload('excel')} className="w-full text-left px-4 py-2 hover:bg-green-50 text-gray-700 text-sm border-b border-gray-100 transition-colors">📊 Excel</button>
                  <button onClick={() => handleDownload('csv')} className="w-full text-left px-4 py-2 hover:bg-yellow-50 text-gray-700 text-sm border-b border-gray-100 transition-colors">📄 CSV</button>
                  <button onClick={() => handleDownload('pdf')} className="w-full text-left px-4 py-2 hover:bg-red-50 text-gray-700 text-sm transition-colors">📕 PDF</button>
                </div>
              )}
            </div>
          </div>

          {/* Effective date label */}
          <div className="mb-2 text-sm text-gray-600">
            {useLatest && latestDay ? (
              <span>Showing data for: <strong>{latestDay.date}/{latestDay.month}/{2000 + latestDay.year}</strong> (Latest)</span>
            ) : (
              <span>
                Showing data for: <strong>
                  {selectedDate !== '' && selectedMonth !== '' && selectedYear !== ''
                    ? `${selectedDate}/${selectedMonth}/${2000 + (typeof selectedYear === 'number' ? selectedYear : 0)}`
                    : selectedMonth !== '' && selectedYear !== ''
                      ? `${selectedMonth}/${2000 + (typeof selectedYear === 'number' ? selectedYear : 0)}`
                      : selectedYear !== ''
                        ? `${2000 + (typeof selectedYear === 'number' ? selectedYear : 0)}`
                        : 'All data'}
                </strong>
              </span>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gradient-to-r from-[#481789] to-[#6A1786] text-white">
                  {visibleColumns.sno && (
                    <th className="px-4 py-3 text-left text-sm font-semibold">S No.</th>
                  )}
                  {visibleColumns.empName && (
                    <th className="px-4 py-3 text-left text-sm font-semibold">Employee Name</th>
                  )}
                  {employeeType === 'regular' ? (
                    <>
                      {visibleColumns.kvName && (
                        <th className="px-4 py-3 text-left text-sm font-semibold">KV Name</th>
                      )}
                      {visibleColumns.roName && (
                        <th className="px-4 py-3 text-left text-sm font-semibold">RO Name</th>
                      )}
                      {visibleColumns.empType && (
                        <th className="px-4 py-3 text-left text-sm font-semibold">Employee Type</th>
                      )}
                      {visibleColumns.presentSchoolName && (
                        <th className="px-4 py-3 text-left text-sm font-semibold">Present School</th>
                      )}
                      {visibleColumns.stateName && (
                        <th className="px-4 py-3 text-left text-sm font-semibold">State</th>
                      )}
                      {visibleColumns.districtName && (
                        <th className="px-4 py-3 text-left text-sm font-semibold">District</th>
                      )}
                      {visibleColumns.empGender && (
                        <th className="px-4 py-3 text-left text-sm font-semibold">Gender</th>
                      )}
                      {visibleColumns.empCategoryName && (
                        <th className="px-4 py-3 text-left text-sm font-semibold">Category</th>
                      )}
                    </>
                  ) : (
                    <>
                      {visibleColumns.empPost && (
                        <th className="px-4 py-3 text-left text-sm font-semibold">Post</th>
                      )}
                      {visibleColumns.subject && (
                        <th className="px-4 py-3 text-left text-sm font-semibold">Subject</th>
                      )}
                      {visibleColumns.presentSchoolName && (
                        <th className="px-4 py-3 text-left text-sm font-semibold">Present School</th>
                      )}
                      {visibleColumns.stateName && (
                        <th className="px-4 py-3 text-left text-sm font-semibold">State</th>
                      )}
                      {visibleColumns.districtName && (
                        <th className="px-4 py-3 text-left text-sm font-semibold">District</th>
                      )}
                      {visibleColumns.dateOfJoining && (
                        <th className="px-4 py-3 text-left text-sm font-semibold">Date of Joining</th>
                      )}
                      {visibleColumns.gender && (
                        <th className="px-4 py-3 text-left text-sm font-semibold">Gender</th>
                      )}
                    </>
                  )}
                  {visibleColumns.totalAttendance && (
                    <th className="px-4 py-3 text-left text-sm font-semibold">Total Attendance</th>
                  )}
                  {visibleColumns.details && (
                    <th className="px-4 py-3 text-left text-sm font-semibold">Details</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="px-4 py-8 text-center text-gray-500">
                      Loading employee data...
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="px-4 py-8 text-center text-gray-500">
                      {searchTerm ? 'No matching employees found' : 'No employee data available'}
                    </td>
                  </tr>
                ) : (
                  filteredData.map((emp, idx) => {
                    return (
                      <tr
                        key={`${emp.empID}-${idx}`}
                        className={idx % 2 === 0 ? 'bg-white' : 'bg-[#edccfc]'}
                      >
                        {visibleColumns.sno && (
                          <td className="px-4 py-3 text-sm text-gray-700">{(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}</td>
                        )}
                        {visibleColumns.empName && (
                          <td className="px-4 py-3 text-sm text-gray-700">{emp.empName}</td>
                        )}

                        {employeeType === 'regular' ? (
                          <>
                            {visibleColumns.kvName && (
                              <td className="px-4 py-3 text-sm text-gray-700">{emp.kvName || 'N/A'}</td>
                            )}
                            {visibleColumns.roName && (
                              <td className="px-4 py-3 text-sm text-gray-700">{emp.roName || 'N/A'}</td>
                            )}
                            {visibleColumns.empType && (
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {emp.empType === 1 ? 'Teaching' : emp.empType === 2 ? 'Non-Teaching' : 'Unknown'}
                              </td>
                            )}
                            {visibleColumns.presentSchoolName && (
                              <td className="px-4 py-3 text-sm text-gray-700">{emp.presentSchoolName || 'N/A'}</td>
                            )}
                            {visibleColumns.stateName && (
                              <td className="px-4 py-3 text-sm text-gray-700">{emp.stateName || 'N/A'}</td>
                            )}
                            {visibleColumns.districtName && (
                              <td className="px-4 py-3 text-sm text-gray-700">{emp.districtName || 'N/A'}</td>
                            )}
                            {visibleColumns.empGender && (
                              <td className="px-4 py-3 text-sm text-gray-700">{emp.empGender === 1 || emp.gender === 1 ? 'Male' : emp.empGender === 2 || emp.gender === 2 ? 'Female' : 'N/A'}</td>
                            )}
                            {visibleColumns.empCategoryName && (
                              <td className="px-4 py-3 text-sm text-gray-700">{emp.empCategoryName || 'N/A'}</td>
                            )}
                          </>
                        ) : (
                          <>
                            {visibleColumns.empPost && (
                              <td className="px-4 py-3 text-sm text-gray-700">{emp.empPost || 'N/A'}</td>
                            )}
                            {visibleColumns.subject && (
                              <td className="px-4 py-3 text-sm text-gray-700">{emp.subject || 'N/A'}</td>
                            )}
                            {visibleColumns.presentSchoolName && (
                              <td className="px-4 py-3 text-sm text-gray-700">{emp.presentSchoolName || 'N/A'}</td>
                            )}
                            {visibleColumns.stateName && (
                              <td className="px-4 py-3 text-sm text-gray-700">{emp.stateName || 'N/A'}</td>
                            )}
                            {visibleColumns.districtName && (
                              <td className="px-4 py-3 text-sm text-gray-700">{emp.districtName || 'N/A'}</td>
                            )}
                            {visibleColumns.dateOfJoining && (
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {emp.dateOfJoining ? new Date(emp.dateOfJoining).toLocaleDateString('en-GB') : 'N/A'}
                              </td>
                            )}
                            {visibleColumns.gender && (
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {emp.gender === 1 ? 'Male' : emp.gender === 2 ? 'Female' : 'N/A'}
                              </td>
                            )}
                          </>
                        )}

                        {visibleColumns.totalAttendance && (
                          <td className="px-4 py-3 text-sm text-gray-700">{emp.totalAttendance || 0}</td>
                        )}

                        {visibleColumns.details && (
                          <td className="px-4 py-3">
                            <button
                              onClick={() => navigate(`/attendance/employee/${emp.empID}`, {
                                state: {
                                  employee: emp,
                                  from: location.pathname + location.search
                                }
                              })}
                              className="text-[#6A1786] hover:text-purple-900 text-sm font-medium hover:underline"
                            >
                              View Details
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#F5E6FA] border-t">
            <div className="text-sm text-gray-700 italic">
              Showing {filteredData.length > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredData.length)} of {filteredData.length} entries
              {searchTerm && ` (filtered from ${employeeData.length} total)`}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="text-sm text-[#6A1786] hover:underline disabled:text-gray-400 disabled:no-underline"
              >
                &lt;&lt; Previous
              </button>
              <span className="text-sm text-gray-700">
                Page {String(currentPage).padStart(2, '0')} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="text-sm text-[#6A1786] hover:underline disabled:text-gray-400 disabled:no-underline"
              >
                Next &gt;&gt;
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
